import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, StatusBadge } from "./shared";
import { PROPERTIES } from "../data/site";
import { X, GitCompareArrows, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { ShortlistButton } from "./ShortlistButton";
import { SaveSearchButton } from "./SavedSearches";
import { useLiveListings } from "../hooks/useLiveListings";

const CATEGORIES = ["All", "Apartment", "Plot", "Villa", "Commercial", "Studio", "Resale"];
const BUDGETS = [
  { label: "Any Budget", min: 0, max: Infinity },
  { label: "Under Rs 60 L", min: 0, max: 6000000 },
  { label: "Rs 60 L - 1 Cr", min: 6000000, max: 10000000 },
  { label: "Rs 1 - 2.5 Cr", min: 10000000, max: 25000000 },
  { label: "Rs 2.5 Cr+", min: 25000000, max: Infinity },
];
const STATUSES = ["Any Status", "Operational", "Under Development", "Announced"];
const LISTINGS = ["New + Resale", "New", "Resale"];

export function Inventory({ onRequestPrice }) {
  const [category, setCategory] = useState("All");
  const [budget, setBudget] = useState(0);
  const [status, setStatus] = useState("Any Status");
  const [listing, setListing] = useState("New + Resale");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const live = useLiveListings();

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const filtered = useMemo(() => {
    const b = BUDGETS[budget];
    return [...live, ...PROPERTIES].filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (status !== "Any Status" && p.status !== status) return false;
      if (listing !== "New + Resale" && p.listing !== listing) return false;
      if (!p.priceOnRequest && (p.priceValue < b.min || p.priceValue >= b.max)) return false;
      if (query && !(p.project + p.config + p.category).toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [category, budget, status, listing, query, live]);

  const toggleCompare = (p) => {
    if (compare.find((x) => x.id === p.id)) return setCompare(compare.filter((x) => x.id !== p.id));
    if (compare.length >= 3) return toast.info("You can compare up to 3 properties.");
    toast.success(`${p.project} added to comparison.`);
    setCompare([...compare, p]);
  };

  return (
    <section id="inventory" className="relative py-28 lg:py-40 bg-[#0A0A0A]" data-testid="inventory-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>Explore GYC Inventory</SectionTag>
        <div className="grid lg:grid-cols-2 gap-8 items-end mb-12">
          <SectionTitle>Find Your Place in <span className="text-gold-gradient italic">Gaur Yamuna City.</span></SectionTitle>
          <Reveal delay={0.15}>
            <p className="text-sm text-[#71717A] leading-relaxed max-w-md lg:ml-auto">
              Sample listings shown for illustration. Live availability and pricing are confirmed
              directly by our advisory desk on enquiry.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10 p-4 border border-[#D4AF37]/15 bg-[#101010] rounded-[2px]" data-testid="inventory-filters">
            <select data-testid="filter-type-select" className="lux-input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="filter-budget-select" className="lux-input" value={budget} onChange={(e) => setBudget(Number(e.target.value))}>
              {BUDGETS.map((b, i) => <option key={b.label} value={i}>{b.label}</option>)}
            </select>
            <select data-testid="filter-status-select" className="lux-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <select data-testid="filter-listing-select" className="lux-input" value={listing} onChange={(e) => setListing(e.target.value)}>
              {LISTINGS.map((l) => <option key={l}>{l}</option>)}
            </select>
            <input
              data-testid="filter-search-input"
              className="lux-input col-span-2 lg:col-span-1"
              placeholder="Search project..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-10 -mt-6" data-testid="inventory-save-search-bar">
            <p className="text-[11px] text-[#71717A]">{filtered.length} listing{filtered.length === 1 ? "" : "s"} match. Save these filters and we&rsquo;ll email you when a new match is listed.</p>
            <SaveSearchButton filters={{ category, budgetLabel: BUDGETS[budget].label, budgetMin: BUDGETS[budget].min, budgetMax: Number.isFinite(BUDGETS[budget].max) ? BUDGETS[budget].max : null, status, listing, query }} />
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="inventory-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                data-testid={`property-card-${p.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="card-lux rounded-[2px] overflow-hidden group"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.project} className="w-full h-full object-cover img-cinematic transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101010] via-transparent to-transparent" />
                  <div className="absolute top-3 left-3 flex items-center gap-2"><StatusBadge status={p.status} />{p.isNew && <span data-testid={`property-new-tag-${p.id}`} className="text-[9px] uppercase tracking-[0.2em] px-2 py-1 bg-[#D4AF37] text-black rounded-full">Just Listed</span>}</div>
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-[0.2em] px-2 py-1 bg-black/60 border border-[#D4AF37]/30 text-[#F3E5AB] rounded-full">{p.listing}</span>
                    <ShortlistButton property={p} />
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">{p.category} &bull; {p.config}</p>
                  <h3 className="font-serif-lux text-xl text-[#F5F5F0] mb-3">{p.project}</h3>
                  <div className="flex items-baseline justify-between mb-4">
                    <span className="text-lg font-semibold text-[#F3E5AB]">{p.price}</span>
                    <span className="text-[11px] text-[#71717A]">{p.size}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-[#71717A] mb-5">
                    <span>Possession: <em className="not-italic text-[#A1A1AA]">{p.possession}</em></span>
                    <span>Facing: <em className="not-italic text-[#A1A1AA]">{p.facing}</em></span>
                    <span>Floor: <em className="not-italic text-[#A1A1AA]">{p.floor}</em></span>
                    <span>Purpose: <em className="not-italic text-[#A1A1AA]">{p.purpose.join(" / ")}</em></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`property-view-btn-${p.id}`}
                      onClick={() => setSelected(p)}
                      className="flex-1 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 transition-colors rounded-[2px]"
                    >
                      View Property
                    </button>
                    <button
                      data-testid={`property-compare-btn-${p.id}`}
                      onClick={() => toggleCompare(p)}
                      aria-label="Compare"
                      className={`p-2.5 border rounded-[2px] transition-colors ${compare.find((x) => x.id === p.id) ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/25 text-[#71717A] hover:text-[#F3E5AB]"}`}
                    >
                      <GitCompareArrows size={15} />
                    </button>
                    <button
                      data-testid={`property-price-btn-${p.id}`}
                      onClick={() => onRequestPrice(p)}
                      aria-label="Request price"
                      className="p-2.5 border border-[#D4AF37]/25 text-[#71717A] hover:text-[#F3E5AB] rounded-[2px] transition-colors"
                    >
                      <ArrowUpRight size={15} />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-[#71717A] text-sm py-16" data-testid="inventory-empty">No listings match these filters. Broaden your search or request off-market inventory.</p>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="property-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-2xl w-full bg-[#111111] border border-[#D4AF37]/25 rounded-[2px] overflow-hidden max-h-[88vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-60">
                <img src={selected.image} alt={selected.project} className="w-full h-full object-cover img-cinematic" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent" />
                <button data-testid="property-modal-close" onClick={() => setSelected(null)} className="absolute top-4 right-4 p-2 bg-black/60 border border-[#D4AF37]/30 rounded-full text-[#F3E5AB]"><X size={16} /></button>
                <div className="absolute bottom-4 left-5"><StatusBadge status={selected.status} /></div>
                <div className="absolute bottom-4 right-5"><ShortlistButton property={selected} /></div>
              </div>
              <div className="p-6 lg:p-8">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{selected.category} &bull; {selected.config} &bull; {selected.listing}</p>
                <h3 className="font-serif-lux text-3xl text-[#F5F5F0] mb-1">{selected.project}</h3>
                <p className="text-xl text-[#F3E5AB] font-semibold mb-6">{selected.price} <span className="text-sm font-normal text-[#71717A]">&bull; {selected.size}</span></p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm mb-6">
                  {[["Possession", selected.possession], ["Floor", selected.floor], ["Facing", selected.facing], ["RERA", selected.rera]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-[#D4AF37]/10 pb-2">
                      <span className="text-[#71717A] text-[11px] uppercase tracking-[0.15em]">{k}</span>
                      <span className="text-[#A1A1AA] text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Key Highlights</p>
                <ul className="space-y-2 mb-8">
                  {selected.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                      <span className="mt-2 h-px w-4 bg-[#D4AF37]/60 shrink-0" /> {h}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3">
                  <button data-testid="property-modal-price-btn" onClick={() => { onRequestPrice(selected); setSelected(null); }} className="px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black rounded-[2px]">Request Price</button>
                  <button data-testid="property-modal-compare-btn" onClick={() => { toggleCompare(selected); setSelected(null); }} className="px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#F3E5AB] rounded-[2px]">Compare</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {compare.length > 0 && !showCompare && (
          <motion.button
            data-testid="compare-open-btn"
            initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
            onClick={() => setShowCompare(true)}
            className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-[65] px-6 py-3.5 bg-[#141414] border border-[#D4AF37]/50 text-[#F3E5AB] text-[11px] uppercase tracking-[0.2em] rounded-full backdrop-blur-md pulse-gold"
          >
            Compare {compare.length} {compare.length === 1 ? "Property" : "Properties"}
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCompare && compare.length > 0 && (
          <motion.div
            data-testid="compare-drawer"
            className="fixed inset-x-0 bottom-0 z-[70] bg-[#0D0D0D]/98 backdrop-blur-md border-t border-[#D4AF37]/30 max-h-[80vh] overflow-y-auto"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="max-w-7xl mx-auto px-5 py-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif-lux text-2xl text-[#F5F5F0]">Compare Before You Decide</h3>
                <button data-testid="compare-close-btn" onClick={() => setShowCompare(false)} className="p-2 border border-[#D4AF37]/30 rounded-full text-[#F3E5AB]"><X size={16} /></button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left">
                      <th className="py-3 pr-4 text-[10px] uppercase tracking-[0.2em] text-[#71717A] font-medium w-36"></th>
                      {compare.map((p) => (
                        <th key={p.id} className="py-3 pr-6 font-serif-lux text-lg text-[#F3E5AB] font-medium">{p.project}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-[#A1A1AA]">
                    {[["Category", (p) => `${p.category} - ${p.config}`], ["Price", (p) => p.price], ["Size", (p) => p.size], ["Possession", (p) => p.possession], ["Status", (p) => p.status], ["Facing", (p) => p.facing], ["Listing", (p) => p.listing], ["Purpose", (p) => p.purpose.join(" / ")]].map(([label, fn]) => (
                      <tr key={label} className="border-t border-[#D4AF37]/10">
                        <td className="py-3 pr-4 text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</td>
                        {compare.map((p) => <td key={p.id} className="py-3 pr-6">{fn(p)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-6 text-[11px] text-[#71717A] leading-relaxed max-w-3xl">
                Comparison is illustrative and based on sample listing data. Budget, development stage, rental
                potential, liquidity, holding period and risk should be reviewed with an advisor before deciding.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
